import { cn } from '@/lib/cn';
import { useTheme } from '@/context/ThemeContext';
import logoCompactLight from '@/assets/logo/logo-compact-light.png';
import logoCompactDark from '@/assets/logo/logo-compact-dark.png';

interface HeaderProps {
  onBack?: () => void;
  onClose?: () => void;
  /** Альт-текст для логотипа. Видимое имя бренда — на самом изображении. */
  title?: string;
}

/**
 * Шапка экранов «АйТи-Профиль». В UX-спеке: «‹ [лого]        ✕».
 *
 * Phase 5 (full dark theme support): логотип переключается между
 * light/dark вариантом через useTheme(). Dimensions идентичны
 * (h-7 w-auto) — нет layout jump при switch'е.
 */
export function Header({ onBack, onClose, title = 'АйТи-Профиль' }: HeaderProps) {
  const { theme } = useTheme();
  const logoSrc = theme === 'dark' ? logoCompactDark : logoCompactLight;

  return (
    <header
      className={cn(
        'sticky top-0 z-10 flex h-12 items-center justify-between gap-2 px-4',
        'bg-background border-b border-border',
      )}
    >
      <div className="flex w-10 justify-start">
        {onBack && (
          <button
            type="button"
            aria-label="Назад"
            onClick={onBack}
            className="flex h-10 w-10 items-center justify-center -ml-2 text-text-primary no-tap-highlight"
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
              <path
                d="M14 5l-7 6 7 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
      {/* Wrapper locked к light compact PNG aspect (1122×268 ≈ 4.19:1).
          Dark compact PNG имеет ~4.70:1 (немного шире/короче) — без fixed
          aspect-ratio dark variant render'ится при h-7 на ~14px wider, что
          даёт небольшой layout-jiggle между темами. С container'ом dark
          logo центрируется внутри light-sized box (object-contain) —
          одинаковая ширина и высота на обеих темах. */}
      <h1 className="flex items-center justify-center min-w-0">
        <div className="h-7 aspect-[1122/268] flex items-center justify-center">
          <img
            src={logoSrc}
            alt={title}
            className="block w-full h-full object-contain select-none"
            draggable={false}
          />
        </div>
      </h1>
      <div className="flex w-10 justify-end">
        {onClose && (
          <button
            type="button"
            aria-label="Закрыть"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center -mr-2 text-text-secondary no-tap-highlight"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>
    </header>
  );
}
